import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { healthsafety } from '@iso/services';
import HazardShape from './HazardShape';
import { parseMetadata, toNumber } from './hazardUtils';
import {
  getSafetyActivityHazardCategoryByHazard,
  getSafetyActivityHazardCategoryLabel,
  getSafetyActivityHazardLabel,
} from '@iso/constants/hazardTypes';

const asArray = (input) => {
  if (Array.isArray(input)) return input;
  if (input != null) return [input];
  return [];
};

const simplifyAttachment = (raw) => {
  if (!raw) return null;

  if (typeof raw === 'string') {
    const name = raw.split('/').pop() || 'attachment';
    return {
      uid: raw,
      id: undefined,
      name,
      url: raw,
      full_path: raw,
      status: 'done',
    };
  }

  const base = raw.attachment ? { ...raw.attachment } : { ...raw };
  const identifier =
    base.id ??
    base.attachment_id ??
    base.uid ??
    base.url ??
    base.full_path ??
    base.path ??
    base.file_path;

  if (!identifier) {
    return null;
  }

  const url = base.url || base.full_path || base.path || base.file_path || undefined;
  const fullPath = base.full_path || base.path || base.file_path || url || undefined;
  const name =
    base.original_filename ||
    base.name ||
    (typeof fullPath === 'string' ? fullPath.split('/').pop() : undefined) ||
    'attachment';

  return {
    id: base.id ?? base.attachment_id ?? undefined,
    uid: String(identifier),
    name,
    url,
    full_path: fullPath,
    status: 'done',
  };
};

const collectAttachments = (activity, metadata) => {
  const sources = [
    metadata.attachments,
    metadata.attachment,
    metadata.attachment_activity,
    activity?.attachments,
    activity?.attachment,
    activity?.attachment_activity,
  ]
    .flatMap(asArray)
    .filter(Boolean);

  const seen = new Set();
  const result = [];

  sources.forEach((item) => {
    const normalized = simplifyAttachment(item);
    if (!normalized) return;
    if (seen.has(normalized.uid)) return;
    seen.add(normalized.uid);
    result.push(normalized);
  });

  return result;
};

const deriveHazardCategoryLabel = (metadata) => {
  const hazardType = metadata.hazard_type;
  const hazardCategory =
    metadata.hazard_category ||
    (hazardType ? getSafetyActivityHazardCategoryByHazard(hazardType) : null);
  return hazardCategory ? getSafetyActivityHazardCategoryLabel(hazardCategory) : null;
};

const deriveHazardDetailLabel = (metadata) => {
  const hazardType = metadata.hazard_type;
  return hazardType ? getSafetyActivityHazardLabel(hazardType) : null;
};

const normalizeHazard = (activity) => {
  if (!activity || (activity.type || '').toLowerCase() !== 'hazard') {
    return null;
  }
  if (activity?.latest_approval?.status !== 'approved') {
    return null;
  }

  const metadata = parseMetadata(activity.metadata);
  const lat = toNumber(
    activity.lat ??
      metadata?.point?.lat ??
      metadata?.point?.latitude
  );
  const lng = toNumber(
    activity.lng ??
      metadata?.point?.lng ??
      metadata?.point?.longitude
  );

  if (lat === null || lng === null) {
    return null;
  }

  const attachments = collectAttachments(activity, metadata);
  const attachmentIds = [
    metadata.attachment_activity_id,
    activity.attachment_activity_id,
  ]
    .flatMap(asArray)
    .map((value) => (value === undefined || value === null ? null : Number(value)))
    .filter((value) => value !== null);

  metadata.point = { lat, lng };
  metadata.attachments = attachments;
  metadata.attachment = attachments;
  metadata.hazard_category = metadata.hazard_category || activity.hazard_category;
  metadata.hazard_type = metadata.hazard_type || activity.hazard_type;
  metadata.hazard_type_other =
    metadata.hazard_type_other || activity.hazard_type_other;
  metadata.actions = metadata.actions || activity.actions;
  metadata.comments = metadata.comments || activity.comments;
  if (attachmentIds.length) {
    metadata.attachment_activity_id = attachmentIds;
  }

  return {
    ...activity,
    lat,
    lng,
    metadata,
    attachments,
    attachment: attachments,
    attachment_activity_id:
      attachmentIds.length ? attachmentIds : activity.attachment_activity_id,
    title:
      metadata.hazard_type_other ||
      metadata.title ||
      activity.assetName ||
      'Hazard',
    hazardCategoryLabel: deriveHazardCategoryLabel(metadata),
    hazardDetailLabel: deriveHazardDetailLabel(metadata),
  };
};

const ActivityHazardMarkers = ({
  propertyId,
  reloadKey,
  onOpenActivityModal,
  onHazardsLoaded,
}) => {
  const [hazards, setHazards] = useState([]);
  const isFetchingRef = useRef(false);

  const loadHazards = useCallback(async () => {
    if (!propertyId) {
      setHazards([]);
      return;
    }
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      // Only fetch activity-based (legacy) hazards here.
      // New hazards from hazardService.getList are rendered by <Hazard />.
      const activitiesRes = await healthsafety
        .getActivityPolicyByProperty(propertyId)
        .catch(() => null);

      if (activitiesRes?.code === '0000') {
        const normalized = (activitiesRes.activities || [])
          .map(normalizeHazard)
          .filter(Boolean);
        setHazards(normalized);
      } else {
        setHazards([]);
      }
    } catch (error) {
      console.error('Error loading hazards:', error);
      setHazards([]);
    } finally {
      isFetchingRef.current = false;
    }
  }, [propertyId]);

  useEffect(() => {
    loadHazards();
  }, [loadHazards, reloadKey]);

  useEffect(() => {
    if (typeof onHazardsLoaded === 'function') {
      onHazardsLoaded(hazards);
    }
  }, [hazards, onHazardsLoaded]);

  const handleMarkerClick = useCallback(
    (hazard) => {
      if (typeof onOpenActivityModal !== 'function') {
        return;
      }
      const fallbackPoint =
        hazard && hazard.lat !== null && hazard.lng !== null
          ? { lat: hazard.lat, lng: hazard.lng }
          : null;
      onOpenActivityModal(hazard, fallbackPoint);
    },
    [onOpenActivityModal]
  );

  const renderMarkers = useMemo(
    () =>
      hazards.map((hazard) => (
          <HazardShape
            key={`activity-hazard-${hazard.id}`}
            hazard={hazard}
            keyPrefix="activity-hazard"
            onClick={() => handleMarkerClick(hazard)}
            riseOnHover
          />
      )).filter(Boolean),
    [hazards, handleMarkerClick]
  );

  return <>{renderMarkers}</>;
};

export default ActivityHazardMarkers;
